import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, Heart } from "lucide-react";
import "./UserMyEvents.css";
import { supabase } from "../../supabase_client.jsx";
import BusinessProfile from "../BusinessProfile/BusinessProfile";

export function isDataVaild(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function setTodayDate(now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function filterEventsByTab({
  events,
  activeTab,
  goingEventIds,
  savedEventIds,
  todayStart,
}) {
  const goingSet = new Set((goingEventIds || []).map(Number));
  const savedSet = new Set((savedEventIds || []).map(Number));

  return (events || []).filter((event) => {
    const eventDate = isDataVaild(event?.date);
    if (!eventDate) return false;

    eventDate.setHours(0, 0, 0, 0);

    const idNum = Number(event?.id);
    const isGoing = goingSet.has(idNum);
    const isSaved = savedSet.has(idNum);

    if (activeTab === "upcoming") return isGoing && eventDate >= todayStart;
    if (activeTab === "saved") return isSaved && !isGoing;
    if (activeTab === "past") return (isGoing || isSaved) && eventDate < todayStart;

    return false;
  });
}

export function filterEventsBySearch(events, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return events || [];
  return (events || []).filter((event) =>
    String(event?.name || "").toLowerCase().includes(q)
  );
}

function getEventTimeLabel(event) {
  const start = event?.start_time || event?.startTime || "TBA";
  const end = event?.end_time || event?.endTime || "";
  return `${start}${end ? ` - ${end}` : ""}`;
}

function getEventLocationLabel(event) {
  return event?.location || event?.address || "";
}

export default function UserMyEvents({
  currentUser,
  onViewSelect,

  // Dependency injection (defaults preserve current behavior)
  supabaseClient = supabase,
  nowProvider = () => new Date(),
}) {
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState([]);
  const [goingEventIds, setGoingEventIds] = useState([]);
  const [savedEventIds, setSavedEventIds] = useState([]);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [suggestedEvents, setSuggestedEvents] = useState(null);
  const [selectedEventContext, setSelectedEventContext] = useState("upcoming");
  const [viewingBusinessName, setViewingBusinessName] = useState(null);
  const [showCheckIn, setShowCheckIn] = useState(false);
  const [checkInCode, setCheckInCode] = useState("");
  const [checkInError, setCheckInError] = useState("");
  const [checkInSuccess, setCheckInSuccess] = useState(false);

  const hiddenInputRef = useRef(null);

  const userId = currentUser?.user?.id || currentUser?.id || null;

  const todayStart = useMemo(() => setTodayDate(nowProvider()), [nowProvider]);

  useEffect(() => {
    if (!userId) {
      console.warn("UserMyEvents: no userId (not logged in?)", currentUser);
      setGoingEventIds([]);
      setSavedEventIds([]);
      setEvents([]);
      return;
    }

    const loadMyEvents = async () => {
      try {
        const [yesRes, bmRes] = await Promise.all([
          supabaseClient.from("marked_yes_events").select("event_id").eq("user_id", userId),
          supabaseClient.from("bookmarked_events").select("event_id").eq("user_id", userId),
        ]);

        if (yesRes.error) console.error("marked_yes_events select error:", yesRes.error.message);
        if (bmRes.error) console.error("bookmarked_events select error:", bmRes.error.message);

        const yesIds = (yesRes.data || [])
          .map((r) => Number(r.event_id))
          .filter((n) => Number.isFinite(n));

        const bmIds = (bmRes.data || [])
          .map((r) => Number(r.event_id))
          .filter((n) => Number.isFinite(n));

        setGoingEventIds(yesIds);
        setSavedEventIds(bmIds);

        const unionIds = Array.from(new Set([...yesIds, ...bmIds]));
        if (unionIds.length === 0) {
          setEvents([]);
          return;
        }

        const { data: eventRows, error: eventErr } = await supabaseClient
          .from("events")
          .select("*")
          .in("id", unionIds);

        if (eventErr) {
          console.error("events select error:", eventErr.message);
          setEvents([]);
          return;
        }

        setEvents(eventRows || []);
      } catch (e) {
        console.error("UserMyEvents unexpected error:", e);
        setEvents([]);
      }

      const { data, error } = await supabase
                .from("events")
                .select("*");
      setSuggestedEvents(data || [])
    };

    loadMyEvents();
  }, [userId, currentUser, supabaseClient, activeTab]);

  const tabFilteredEvents = useMemo(() => {
    return filterEventsByTab({
      events,
      activeTab,
      goingEventIds,
      savedEventIds,
      todayStart,
    });
  }, [events, activeTab, goingEventIds, savedEventIds, todayStart]);

  const filteredEvents = useMemo(() => {
    return filterEventsBySearch(tabFilteredEvents, searchQuery);
  }, [tabFilteredEvents, searchQuery]);

  const savedSidebarEvents = useMemo(() => {
    const savedOnlyEvents = filterEventsByTab({
      events,
      activeTab: "saved",
      goingEventIds,
      savedEventIds,
      todayStart,
    });

    return filterEventsBySearch(savedOnlyEvents, searchQuery);
  }, [events, goingEventIds, savedEventIds, todayStart, searchQuery]);

  const openEventDetails = (event, context) => {
    setSelectedEvent(event);
    setSelectedEventContext(context);
    setShowCheckIn(false);
    setCheckInCode("");
    setCheckInError("");
    setCheckInSuccess(false);
  };

  const handleYesGoing = async (event) => {
    if (!userId) return;
    try {
      const { error } = await supabaseClient
        .from("marked_yes_events")
        .insert({ user_id: userId, event_id: event.id });
      if (error) {
        alert("Failed to mark as going: " + error.message);
        return;
      }
      setGoingEventIds((prev) => [...prev, Number(event.id)]);
      setSelectedEvent(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleUnsave = async (event) => {
    if (!userId) return;
    try {
      const { error } = await supabaseClient
        .from("bookmarked_events")
        .delete()
        .eq("user_id", userId)
        .eq("event_id", event.id);
      if (error) {
        alert("Failed to unsave: " + error.message);
        return;
      }
      setSavedEventIds((prev) => prev.filter((id) => id !== Number(event.id)));
      setEvents((prev) => {
        if (!goingEventIds.includes(Number(event.id))) {
          return prev.filter((e) => e.id !== event.id);
        }
        return prev;
      });
      setSelectedEvent(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCancelGoing = async (event) => {
    if (!userId) return;
    try {
      const { error } = await supabaseClient
        .from("marked_yes_events")
        .delete()
        .eq("user_id", userId)
        .eq("event_id", event.id);
      if (error) {
        alert("Failed to cancel: " + error.message);
        return;
      }
      setGoingEventIds((prev) => prev.filter((id) => id !== Number(event.id)));
      setEvents((prev) => {
        if (!savedEventIds.includes(Number(event.id))) {
          return prev.filter((e) => e.id !== event.id);
        }
        return prev;
      });
      setSelectedEvent(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCheckIn = () => {
    setCheckInCode("");
    setCheckInError("");
    setCheckInSuccess(false);
    setShowCheckIn(true);
  };

  const handleCheckInCodeChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
    setCheckInCode(val);
    setCheckInError("");
  };

  const handleCheckInSubmit = async () => {
    if (checkInCode.length < 4) {
      setCheckInError("Please enter the 4-character code.");
      const correctCode = (selectedEvent.checkin_code || "").toUpperCase().trim();
      console.log("User entered:", JSON.stringify(checkInCode));
      console.log("Stored code:", JSON.stringify(correctCode));
      return;
    }

    // Validate against the stored code on the event
    const correctCode = (selectedEvent.checkin_code || "").toUpperCase();
    if (checkInCode.toUpperCase() !== correctCode) {
      setCheckInError("Incorrect code. Please try again.");
      return;
    }

    // Check if already checked in
    const { data: existing } = await supabase
      .from("checkins")
      .select("id")
      .eq("event_id", selectedEvent.id)
      .eq("user_id", userId)
      .maybeSingle();

    if (existing) {
      setCheckInSuccess(true);
      setCheckInError("");
      return; // already checked in, just show success
    }

    // Record the check-in
    const { error } = await supabase
      .from("checkins")
      .insert({ event_id: selectedEvent.id, user_id: userId });

    if (error) {
      setCheckInError("Check-in failed. Please try again.");
      return;
    }

    setCheckInSuccess(true);
    setCheckInError("");
  };

  const closePopup = () => {
    setSelectedEvent(null);
    setSelectedEventContext(activeTab);
    setShowCheckIn(false);
    setCheckInCode("");
    setCheckInError("");
    setCheckInSuccess(false);
  };

  return (
    <div className="my-events-container">
      <div className="home-card">
          {/* App title banner */}
          <div className="app-title-banner">
            <h1 className="app-title-text">Do You Wanna Go?</h1>
          </div>

        <div className="calendar-top-nav">
          <div className="nav-left">
            <button className="nav-tab" onClick={() => onViewSelect("user")}>
              Calendar
            </button>
            <button className="nav-tab active">My Events</button>
            <button className="nav-tab" onClick={() => onViewSelect("connections")}>
              My Connections
            </button>
          </div>

          <div className="nav-right">
            <button
              type="button"
              className="nav-icon-circle nav-icon-button nav-notification-button"
              aria-label="Notifications"
              onClick={() => onViewSelect("notifications")}
            >
              <svg
                className="nav-bell-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </button>

            <div className="nav-avatar-circle">
              <button
                className="nav-tab nav-profile-button"
                onClick={() => onViewSelect("user-profile")}
                aria-label="Profile"
              >
                <svg
                  className="nav-user-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="my-events-layout">
          <div className="my-events-main">
            <div className="my-events-main-header">
              <div>
                <h1 className="my-events-title">My Events</h1>
                <p className="my-events-subtitle">Manage and view events you want to go to!</p>
              </div>

              <div className="calendar-search-row">
                <div className="search-box">
                  <Search className="icon-search" />
                  <input
                    placeholder="Search Event..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="my-events-tabs">
              <button
                className={activeTab === "upcoming" ? "tab active" : "tab inactive"}
                onClick={() => setActiveTab("upcoming")}
              >
                Upcoming Events
              </button>

              <button
                className={activeTab === "past" ? "tab active" : "tab inactive"}
                onClick={() => setActiveTab("past")}
              >
                Past Events
              </button>
            </div>

            <div className="events-list">
              {filteredEvents.map((event) => (
                <div
                  key={event.id}
                  className="event-row"
                  onClick={() => openEventDetails(event, activeTab)}
                >
                  {event.image_url ? (
                    <img src={event.image_url} alt={event.name} className="event-image" />
                  ) : (
                    <div className="event-image-placeholder" />
                  )}

                  <div className="event-info">
                    <h3 className="event-name">{event.name}</h3>

                    <p className="event-time">
                      {event.date} {" | "} {getEventTimeLabel(event)}
                    </p>

                    <p className="event-address">{getEventLocationLabel(event)}</p>
                  </div>
                </div>
              ))}

              {filteredEvents.length === 0 && (
                <p className="no-events">
                  {activeTab === "upcoming"
                    ? "No upcoming events found"
                    : "No past events found"}
                </p>
              )}
            </div>
          </div>

          <aside className="my-events-sidebar">
            <div className="sidebar-card">
              <div className="sidebar-title">Saved Events</div>
              <div className="sidebar-subtitle">Events you’ve bookmarked</div>

              {savedSidebarEvents.length === 0 ? (
                <div className="sidebar-empty">No saved events yet.</div>
              ) : (
                <div className="saved-events-list">
                  {savedSidebarEvents.map((event) => (
                    <div
                      key={event.id}
                      className="saved-event-row"
                      onClick={() => openEventDetails(event, "saved")}
                    >
                      {event.image_url ? (
                        <img
                          src={event.image_url}
                          alt={event.name}
                          className="saved-event-thumb"
                        />
                      ) : (
                        <div className="saved-event-thumb-placeholder" />
                      )}

                      <div className="saved-event-content">
                        <div className="saved-event-name">{event.name}</div>
                        <div className="saved-event-meta">{event.date}</div>
                        <div className="saved-event-location">{getEventLocationLabel(event)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="sidebar-divider"></div>

              <div className="sidebar-title">Suggested Events</div>
              <div className="sidebar-subtitle">A spot for future recommendations</div>


                <div className="saved-events-list">
                  {suggestedEvents?.sort(function(a, b) {
                    return new Date(b.date) - new Date(a.date)
                  }).slice(0,3).map((event) => (
                    <div
                      key={event.id}
                      className="saved-event-row"
                      onClick={() => openEventDetails(event, "saved")}
                    >
                      {event.image_url ? (
                        <img
                          src={event.image_url}
                          alt={event.name}
                          className="saved-event-thumb"
                        />
                      ) : (
                        <div className="saved-event-thumb-placeholder" />
                      )}

                      <div className="saved-event-content">
                        <div className="saved-event-name">{event.name}</div>
                        <div className="saved-event-meta">{event.date}</div>
                        <div className="saved-event-location">{getEventLocationLabel(event)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              {/*<div className="suggested-events-placeholder">
                <div className="suggested-placeholder-card">
                  <div className="suggested-placeholder-badge">Coming soon</div>
                  <div className="suggested-placeholder-title">
                    Suggested events will show up here.
                  </div>
                  <div className="suggested-placeholder-text">
                    This section is only a placeholder for now, so your existing event
                    functionality stays exactly where it already works.
                  </div>
                </div>
              </div>*/}
            </div>
          </aside>
        </div>

        {selectedEvent && !showCheckIn && (
          <div className="popup-overlay" onClick={closePopup}>
            <div className="popup" onClick={(e) => e.stopPropagation()}>
              <div className="popup-image-wrapper">
                <button className="close-btn" onClick={closePopup}>
                  ✕
                </button>
                {selectedEvent.image_url ? (
                  <img
                    src={selectedEvent.image_url}
                    alt={selectedEvent.name}
                    className="popup-image"
                  />
                ) : (
                  <div className="popup-image-placeholder" />
                )}
              </div>

              <div className="popup-content">
                <div className="popup-header">
                  <div>
                    <h2 className="popup-title">{selectedEvent.name}</h2>
                    <span
                      className={`popup-badge ${
                        selectedEventContext === "upcoming"
                          ? "badge-approved"
                          : selectedEventContext === "saved"
                            ? "badge-pending"
                            : "badge-past"
                      }`}
                    >
                      {selectedEventContext === "upcoming"
                        ? "Upcoming"
                        : selectedEventContext === "saved"
                          ? "Saved"
                          : "Past"}
                    </span>
                  </div>
                </div>

                <div className="popup-divider" />

                <div className="popup-details">
                  {selectedEvent.date && (
                    <div className="popup-row">
                      <span className="popup-label">Date</span>
                      <span className="popup-value">{selectedEvent.date}</span>
                    </div>
                  )}
                  {(selectedEvent.start_time ||
                    selectedEvent.end_time ||
                    selectedEvent.startTime ||
                    selectedEvent.endTime) && (
                    <div className="popup-row">
                      <span className="popup-label">Time</span>
                      <span className="popup-value">{getEventTimeLabel(selectedEvent)}</span>
                    </div>
                  )}
                  {getEventLocationLabel(selectedEvent) && (
                    <div className="popup-row">
                      <span className="popup-label">Location</span>
                      <span className="popup-value">{getEventLocationLabel(selectedEvent)}</span>
                    </div>
                  )}
                  {selectedEvent.event_owner && (
                    <div className="popup-row">
                      <span className="popup-label">Event Owner</span>
                      <span
                        className="popup-value event-owner-link"
                        onClick={() => {
                          const name = selectedEvent.event_owner;
                          if (name) setViewingBusinessName(name);
                        }}
                      >
                        {selectedEvent.event_owner}
                      </span>
                    </div>
                  )}
                  {selectedEvent.description && (
                    <div className="popup-row popup-row-col">
                      <span className="popup-label">Description</span>
                      <span className="popup-value">{selectedEvent.description}</span>
                    </div>
                  )}
                </div>

                <div className="admin-modal-footer">
                  {selectedEventContext === "upcoming" && (
                    <>
                      <button className="admin-approve-btn" onClick={handleCheckIn}>
                        Check In!
                      </button>
                      <button
                        className="admin-delete-btn"
                        onClick={() => handleCancelGoing(selectedEvent)}
                      >
                        Cancel
                      </button>
                    </>
                  )}

                  {selectedEventContext === "saved" && (
                    <div className="popup-going-row">
                      <span className="popup-going-text">Do you wanna go?</span>
                      <div className="popup-going-actions">
                        <button
                          className="btn-yes-pill"
                          onClick={() => handleYesGoing(selectedEvent)}
                        >
                          Yes
                        </button>
                        <button
                          className="bookmark-toggle active"
                          onClick={() => handleUnsave(selectedEvent)}
                        >
                          <Heart className="bookmark-icon" />
                          <span>Saved</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedEventContext === "past" && (
                    <button className="admin-cancel-btn" onClick={closePopup}>
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {showCheckIn && selectedEvent && (
          <div className="popup-overlay" onClick={closePopup}>
            <div className="popup admin-modal-narrow" onClick={(e) => e.stopPropagation()}>
              <button className="close-btn" onClick={closePopup}>
                ✕
              </button>

              <div className="popup-header">
                <div className="popup-avatar checkin-avatar">🎟️</div>
                <div>
                  <h2 className="popup-title">Check In</h2>
                  <span className="popup-badge badge-approved">Enter Code</span>
                </div>
              </div>

              <div className="popup-divider" />

              <div className="popup-details">
                <div
                  className="popup-row popup-row-col"
                  style={{ textAlign: "center", paddingBottom: 8 }}
                >
                  <span className="popup-label" style={{ marginBottom: 4 }}>
                    Checking into
                  </span>
                  <span
                    className="popup-value"
                    style={{ fontWeight: 600, fontSize: "1.05rem" }}
                  >
                    {selectedEvent.name}
                  </span>
                </div>

                <div className="checkin-code-container">
                  <p className="checkin-instructions">
                    Enter the 4-character check-in code provided at the event
                  </p>

                  <div
                    className="checkin-code-boxes"
                    onClick={() => hiddenInputRef.current?.focus()}
                  >
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`checkin-code-box ${
                          checkInCode[i] ? "filled" : ""
                        } ${
                          checkInError && checkInCode.length === 4 ? "error" : ""
                        } ${checkInSuccess ? "success" : ""}`}
                      >
                        {checkInCode[i] || ""}
                      </div>
                    ))}
                  </div>

                  <input
                    ref={hiddenInputRef}
                    className="checkin-hidden-input"
                    type="text"
                    value={checkInCode}
                    onChange={handleCheckInCodeChange}
                    maxLength={4}
                    autoFocus
                    autoComplete="off"
                    spellCheck={false}
                  />

                  <p className="checkin-hint">2 letters + 2 numbers (e.g. AB12)</p>

                  {checkInError && <p className="checkin-error">{checkInError}</p>}

                  {checkInSuccess && (
                    <p className="checkin-success">✓ Checked in successfully!</p>
                  )}
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  className="admin-approve-btn"
                  onClick={handleCheckInSubmit}
                  disabled={checkInCode.length < 4}
                >
                  Submit
                </button>
                <button className="admin-cancel-btn" onClick={() => setShowCheckIn(false)}>
                  Back
                </button>
              </div>
            </div>
          </div>
        )}

        {viewingBusinessName && (
          <div
            className="business-full-popup-overlay"
            onClick={() => setViewingBusinessName(null)}
          >
            <div
              className="business-full-popup-container"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="bp-close-btn" onClick={() => setViewingBusinessName(null)}>
                ×
              </button>
              <BusinessProfile businessId={viewingBusinessName} readOnly={true} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}